/**
 * GeneratorDatabase - Generator Equipment Database
 *
 * Full CRUD interface for managing generator/genset equipment in the database.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { PowerSettingsNew as GeneratorIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import { useAuth } from '@vortx/shared-ui';
import EquipmentTable from '../EquipmentTable';
import EquipmentForm from './EquipmentForm';
import { API_BASE_URL } from '../../../services/api';

const GENERATOR_COLUMNS = [
    { id: 'manufacturer', label: 'Manufacturer', width: '18%' },
    { id: 'model', label: 'Model', width: '18%' },
    { id: 'power_kw', label: 'Power (kW)', width: '12%', highlight: true, render: (v) => v?.toLocaleString() },
    { id: 'fuel_type', label: 'Fuel Type', width: '14%' },
    { id: 'equipment_cost', label: 'Equipment ($M)', width: '14%', highlight: true, render: (v) => v ? `$${(v/1000000).toFixed(2)}M` : '-' },
    { id: 'status', label: 'Status', width: '10%' }
];

function GeneratorDatabase() {
    const { currentUser } = useAuth();
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingEquipment, setEditingEquipment] = useState(null);

    const fetchEquipment = useCallback(async () => {
        console.log('[GeneratorDatabase] Fetching equipment list...');
        setLoading(true);
        setError(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/equipment/generator?status=active`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch generator equipment');
            const data = await res.json();
            console.log('[GeneratorDatabase] Fetched equipment:', data.equipment?.length, 'items');

            // Sort alphabetically by manufacturer, then by model
            const sorted = (data.equipment || []).sort((a, b) => {
                const manufacturerCompare = (a.manufacturer || '').localeCompare(b.manufacturer || '');
                if (manufacturerCompare !== 0) return manufacturerCompare;
                return (a.model || '').localeCompare(b.model || '');
            });

            setEquipment(sorted);
        } catch (err) {
            console.error('[GeneratorDatabase] Error fetching equipment:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchEquipment();
    }, [fetchEquipment]);

    const handleCreate = () => {
        setEditingEquipment(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditingEquipment(item);
        setShowForm(true);
    };

    const handleCopy = (item) => {
        const { id, created_at, created_by, created_by_email, updated_at, version_history, ...copiedData } = item;
        setEditingEquipment(copiedData);
        setShowForm(true);
    };

    const handleSave = async (data) => {
        try {
            // Clean data: remove metadata fields and convert string numbers to actual numbers
            const { id, created_at, created_by, created_by_email, updated_at, version_history, ...cleanedData } = data;

            const numericFields = ['power_kw', 'fuel_efficiency_kwh_per_gal', 'fuel_efficiency_kwh_per_mmbtu',
                                   'equipment_cost', 'installation_cost', 'warranty_years', 'noise_level_db', 'weight_kg'];
            numericFields.forEach(field => {
                if (cleanedData[field] !== null && cleanedData[field] !== undefined && cleanedData[field] !== '') {
                    cleanedData[field] = Number(cleanedData[field]);
                }
            });

            const token = await currentUser.getIdToken();
            const url = editingEquipment?.id
                ? `${API_BASE_URL}/api/site_dna/v1/equipment/generator/${editingEquipment.id}`
                : `${API_BASE_URL}/api/site_dna/v1/equipment/generator`;
            const method = editingEquipment?.id ? 'PUT' : 'POST';

            console.log('[GeneratorDatabase] Saving equipment:', { method, url, hasId: !!editingEquipment?.id, data: cleanedData });

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(cleanedData)
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
                console.error('[GeneratorDatabase] Save failed:', { status: res.status, errorData });
                throw new Error(errorData.error || `Failed to save equipment (${res.status})`);
            }

            console.log('[GeneratorDatabase] Save successful, refreshing list...');

            // Close form first
            setShowForm(false);
            setEditingEquipment(null);

            // Then refresh the list
            await fetchEquipment();
        } catch (err) {
            console.error('[GeneratorDatabase] Error saving equipment:', err);
            alert(`Error: ${err.message}`);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Delete ${item.manufacturer} ${item.model}?`)) return;

        try {
            console.log('[GeneratorDatabase] Deleting equipment:', item.id);
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/equipment/generator/${item.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errorData.error || 'Failed to delete equipment');
            }

            console.log('[GeneratorDatabase] Delete successful, refreshing list...');
            await fetchEquipment();
        } catch (err) {
            console.error('[GeneratorDatabase] Error deleting equipment:', err);
            alert(`Error: ${err.message}`);
        }
    };

    return (
        <Box>
            <Alert
                severity="info"
                icon={<GeneratorIcon />}
                sx={{
                    mb: 3,
                    bgcolor: alpha(vortxColors.gold, 0.1),
                    color: vortxColors.white,
                    '& .MuiAlert-icon': { color: vortxColors.gold }
                }}
            >
                Manage generator/genset specifications, fuel efficiency, and pricing.
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <EquipmentTable
                title="Generators"
                description="Manage generator specifications, fuel efficiency, and pricing."
                columns={GENERATOR_COLUMNS}
                data={equipment}
                loading={loading}
                onAdd={handleCreate}
                onEdit={handleEdit}
                onCopy={handleCopy}
                onDelete={handleDelete}
                onRefresh={fetchEquipment}
                emptyMessage="No generators in database. Click 'Add Entry' to create one."
            />

            {showForm && (
                <EquipmentForm
                    open={showForm}
                    type="generator"
                    equipment={editingEquipment}
                    onSave={handleSave}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingEquipment(null);
                    }}
                />
            )}
        </Box>
    );
}

export default GeneratorDatabase;
